import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {TargetRowAndStorables} from 'notebook/structured-tables/action-functions/inventoryShortcutsActionFunction';
import {
  ColumnWithPositionalIndex,
  StructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import MoveStorablesModal from 'samples/MoveStorablesModal';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {
  MoveStorablesFromStructuredTableModalQuery,
  MoveStorablesFromStructuredTableModalQueryVariables,
} from './__generated/MoveStorablesFromStructuredTableModalQuery';

interface MoveStorablesFromStructuredTableProps<TTableMetadata, TRowMetadata>
  extends Omit<ModalTypeProps<TTableMetadata, TRowMetadata>, 'storables'> {
  modalType: React.ComponentType<ModalTypeProps<TTableMetadata, TRowMetadata>>;
}

export interface ModalTypeProps<TTableMetadata, TRowMetadata> {
  storables: Array<StorableSummaryGraphQL>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  targetStorableColumnWithIndex: ColumnWithPositionalIndex;
  targetRowsAndStorables: Array<TargetRowAndStorables<TRowMetadata>>;
  onRequestClose: () => void;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
  onSuccess?: () => void;
}

interface QueryingProps {
  accessibleStorables: Array<StorableSummaryGraphQL>;
}

export const MOVE_STORABLES_FROM_STRUCTURED_TABLE_MODAL_QUERY = gql`
  query MoveStorablesFromStructuredTableModalQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $storableIds) {
      ...StorableSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StorableSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function MoveStorablesFromStructuredTableModal<TTableMetadata, TRowMetadata>(
  props: MoveStorablesFromStructuredTableProps<TTableMetadata, TRowMetadata> & QueryingProps
): JSX.Element | null {
  const {modalType: ModalType, accessibleStorables} = props;
  return <ModalType {...props} storables={accessibleStorables} />;
}

MoveStorablesFromStructuredTableModal.defaultProps = {
  modalType: MoveStorablesModal,
};

export default function QueryingMoveStorablesFromStructuredTableModal<TTableMetadata, TRowMetadata>(
  props: MoveStorablesFromStructuredTableProps<TTableMetadata, TRowMetadata>
): JSX.Element | null {
  const allTargetStorablesIds: Array<string> = props.targetRowsAndStorables.flatMap(
    (targetStorableIdsAndRow) => targetStorableIdsAndRow.targetStorables.map(({id}) => id)
  );
  // Filter out duplicates before fetching
  const uniqueTargetStorableIds = [...new Set(allTargetStorablesIds)];

  const result = useBenchlingQuery<
    MoveStorablesFromStructuredTableModalQuery,
    MoveStorablesFromStructuredTableModalQueryVariables
  >(MOVE_STORABLES_FROM_STRUCTURED_TABLE_MODAL_QUERY, {variables: {storableIds: uniqueTargetStorableIds}});

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (result.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Could not load inventory items." />;
  }
  if (result.type === QueryResultType.SUCCESS) {
    const accessibleStorablesGraphQL = accessibleObjs(result.data.storables);
    // Opening this modal should be disabled if not all target storables are writable
    assert(
      accessibleStorablesGraphQL.length === result.data.storables.length,
      'Expected all target storables to be accessible.'
    );

    return (
      <MoveStorablesFromStructuredTableModal {...props} accessibleStorables={accessibleStorablesGraphQL} />
    );
  }

  assert(false, 'unhandled query result type');
}

QueryingMoveStorablesFromStructuredTableModal.defaultProps = {
  modalType: MoveStorablesModal,
};
